import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import { getFirebaseAuth } from './firebase.js';
import { finishGlobalLoading, startGlobalLoading } from '../store/uiStore.js';

const origin = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '';
const SESSION_STORAGE_KEY = 'ig_session_id_v1';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    const current = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (current && /^[a-zA-Z0-9_-]{12,128}$/.test(current)) return current;
    const generated =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(SESSION_STORAGE_KEY, generated);
    return generated;
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
}

export const api = axios.create({
  baseURL: origin ? `${origin}/api/v1` : '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

export type IgRequestConfig = AxiosRequestConfig & {
  /** Opt-in: render full-screen global loader for this request. */
  showGlobalLoader?: boolean;
  suppressGlobalLoader?: boolean;
  /** Force-refresh Firebase ID token before attaching Authorization (post sign-in). */
  forceAuthRefresh?: boolean;
  /**
   * Opt-out of the global error toast so the caller can surface its own
   * message (e.g. binary/blob responses where the interceptor can't read the
   * error body, or flows with specific per-code messaging).
   */
  suppressErrorToast?: boolean;
};

type TrackedRequestConfig = IgRequestConfig & {
  __igLoaderTracked?: boolean;
};

type TrackedInternalRequestConfig = InternalAxiosRequestConfig & {
  showGlobalLoader?: boolean;
  suppressGlobalLoader?: boolean;
  forceAuthRefresh?: boolean;
  suppressErrorToast?: boolean;
  __igLoaderTracked?: boolean;
};

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const tracked = config as TrackedInternalRequestConfig;
  const shouldTrackWithGlobalLoader = tracked.showGlobalLoader === true && !tracked.suppressGlobalLoader;
  if (shouldTrackWithGlobalLoader && !tracked.__igLoaderTracked) {
    startGlobalLoading();
    tracked.__igLoaderTracked = true;
  }
  try {
    const user = getFirebaseAuth().currentUser;
    const sessionId = getOrCreateSessionId();
    if (typeof (tracked.headers as { set?: (name: string, value: string) => void }).set === 'function') {
      (tracked.headers as { set: (name: string, value: string) => void }).set('X-IG-Session-Id', sessionId);
    } else {
      (tracked.headers as Record<string, string>)['X-IG-Session-Id'] = sessionId;
    }
    if (user) {
      /** `false` avoids forcing a refresh on every request (e.g. tight poll loops). */
      const token = await user.getIdToken(tracked.forceAuthRefresh === true);
      if (typeof (tracked.headers as { set?: (name: string, value: string) => void }).set === 'function') {
        (tracked.headers as { set: (name: string, value: string) => void }).set(
          'Authorization',
          `Bearer ${token}`,
        );
      } else {
        (tracked.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }
    return tracked;
  } catch (error) {
    if (tracked.__igLoaderTracked) {
      finishGlobalLoading();
      tracked.__igLoaderTracked = false;
    }
    return Promise.reject(error);
  }
});

api.interceptors.response.use(
  (res) => {
    const tracked = res.config as TrackedRequestConfig;
    if (tracked.__igLoaderTracked) {
      finishGlobalLoading();
      tracked.__igLoaderTracked = false;
    }
    return res;
  },
  (error: AxiosError<{ code?: string; error?: string }>) => {
    const tracked = error.config as TrackedRequestConfig | undefined;
    if (tracked?.__igLoaderTracked) {
      finishGlobalLoading();
      tracked.__igLoaderTracked = false;
    }
    const status = error.response?.status;
    const code = error.response?.data?.code;

    if (status === 401) {
      const requestUrl = String(error.config?.url ?? '');
      const path = window.location.pathname;
      const isAuthBootstrap = requestUrl.includes('/auth/sync');
      const onAuthPage = path === '/login' || path === '/register';

      // Avoid reload loop: Firebase session can remain while /auth/sync fails (e.g. API
      // misconfiguration). Let auth store sign out and show errors on auth pages instead.
      if (!isAuthBootstrap && !onAuthPage) {
        window.location.assign('/login');
      }
      return Promise.reject(error);
    }
    if (status === 503 && code === 'FIREBASE_ADMIN_NOT_CONFIGURED') {
      toast.error(
        error.response?.data?.error ??
          'The API cannot verify sign-in. Add Firebase Admin credentials to apps/api/.env.',
      );
      return Promise.reject(error);
    }
    if (status === 403 && code === 'PACK_EXHAUSTED') {
      window.location.assign('/payment');
      return Promise.reject(error);
    }
    if (status === 403 && code === 'EMAIL_NOT_VERIFIED') {
      const path = window.location.pathname;
      if (path !== '/verify-email' && path !== '/login' && path !== '/register') {
        window.location.assign('/verify-email');
      }
      return Promise.reject(error);
    }
    if (status === 404 && code === 'NOT_FOUND') {
      return Promise.reject(error);
    }
    if (status === 503 && code === 'QUEUE_UNAVAILABLE') {
      // Let callers handle this specifically (e.g. show a targeted toast, keep
      // polling). The global interceptor intentionally does NOT auto-toast here
      // to avoid duplicate messages and spamming toasts on polling loops.
      return Promise.reject(error);
    }
    if (tracked?.suppressErrorToast) {
      return Promise.reject(error);
    }

    if (error.response?.data?.error) {
      toast.error(error.response.data.error);
    } else {
      toast.error('Something went wrong');
    }

    return Promise.reject(error);
  },
);

// Deduplicates concurrent GET requests to the same URL so that two callers
// hitting the same endpoint within the same tick share one in-flight promise
// instead of creating duplicate network requests. The entry is removed as
// soon as the promise settles (success or error), so subsequent independent
// calls (e.g. after a mutation) always go to the network.
const inflightGets = new Map<string, Promise<unknown>>();

export async function apiGet<T>(url: string, config?: IgRequestConfig) {
  // Only deduplicate simple, param-free GET calls — as soon as extra config
  // options are present we can't safely share the request.
  const canDedupe = !config || Object.keys(config).length === 0;
  if (canDedupe && inflightGets.has(url)) {
    return inflightGets.get(url) as Promise<T>;
  }
  const req = api.get<T>(url, config).then((res) => res.data);
  if (canDedupe) {
    inflightGets.set(url, req as Promise<unknown>);
    req.finally(() => inflightGets.delete(url));
  }
  return req;
}

export async function apiPost<T>(url: string, data?: unknown, config?: IgRequestConfig) {
  const res = await api.post<T>(url, data, config);
  return res.data;
}

export async function apiPatch<T>(url: string, data?: unknown, config?: IgRequestConfig) {
  const res = await api.patch<T>(url, data, config);
  return res.data;
}

export async function apiPut<T>(url: string, data?: unknown, config?: IgRequestConfig) {
  const res = await api.put<T>(url, data, config);
  return res.data;
}

export async function apiDelete<T>(url: string, config?: IgRequestConfig) {
  const res = await api.delete<T>(url, config);
  return res.data;
}

export function getApiOrigin() {
  return origin;
}

/** Build Authorization + session headers for raw `fetch` calls (SSE, audio, FormData). */
export async function getFetchAuthHeaders(
  options: { forceAuthRefresh?: boolean; accept?: string; contentType?: string } = {},
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'X-IG-Session-Id': getOrCreateSessionId(),
  };
  if (options.accept) headers.Accept = options.accept;
  if (options.contentType) headers['Content-Type'] = options.contentType;

  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  headers.Authorization = `Bearer ${await user.getIdToken(options.forceAuthRefresh === true)}`;
  return headers;
}

/** Mirror the axios 401 handler for authenticated fetch calls. */
export async function throwIfFetchNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  if (res.status === 401 && typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path !== '/login' && path !== '/register') {
      window.location.assign('/login');
    }
  }
  const body = await res.json().catch(() => null);
  const message =
    (body as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
  throw new Error(message);
}

/** Authenticated JSON file download (e.g. GDPR export). */
export async function apiDownloadBlob(url: string): Promise<Blob> {
  const res = await api.get(url, { responseType: 'blob' });
  return res.data as Blob;
}
